import React, { useState } from 'react';
import { useForm } from 'antd/es/form/Form';
import { Button, Form, Input, Space } from 'antd';
import { ReactComponent as TwentyFourHourSvg } from '../../../assets/svg/24hr.svg';
import { useAsyncCallback } from 'react-async-hook';
import { onboardingApi } from '../../../api';
import { CreateApplication } from '@quick/client';
import ModalLayout from '../components/ModalLayout';

enum STEP {
  COMMUNITY_INFORMATION = 'COMMUNITY_INFORMATION',
  PRIMARY_CONTACT = 'PRIMARY_CONTACT',
  SUCCESS = 'SUCCESS',
}

interface CommunityApplicationProps {
  onComplete: () => void;
  switchFlow: () => void;
}

const CreateFlow = ({ onComplete, switchFlow }: CommunityApplicationProps) => {
  const [form] = useForm<CreateApplication>();
  const [step, setStep] = useState(STEP.COMMUNITY_INFORMATION);
  const applicationState = useAsyncCallback(
    async () => {
      return onboardingApi.createApplication({
        createApplication: form.getFieldsValue(true),
      });
    },
    {
      onSuccess: () => setStep(STEP.SUCCESS),
    }
  );

  const onClickNextStep = async (step: STEP) => {
    try {
      await form.validateFields();
      setStep(step);
    } catch (e) {}
  };

  const onSubmit = async () => {
    try {
      await form.validateFields();
      await applicationState.execute();
    } catch (e) {}
  };

  switch (step) {
    case STEP.COMMUNITY_INFORMATION:
      return (
        <ModalLayout
          header={<h6>Tell us about your community</h6>}
          content={
            <Form layout="vertical" preserve colon={false} form={form} requiredMark={false}>
              <Form.Item
                preserve
                required
                label="Community name"
                name="name"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                preserve
                required
                label="Address"
                name="address"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Form>
          }
          footer={
            <Space>
              <Button onClick={() => onClickNextStep(STEP.PRIMARY_CONTACT)} type="primary">
                Next
              </Button>
              <Button onClick={switchFlow} type="text">
                Previous
              </Button>
            </Space>
          }
        />
      );
    case STEP.PRIMARY_CONTACT:
      return (
        <ModalLayout
          header={<h6>Who should we contact</h6>}
          content={
            <Form layout="vertical" colon={false} form={form} requiredMark={false} preserve={false}>
              <Form.Item
                preserve
                required
                label="Name"
                name="contact"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                preserve
                required
                label="Phone number"
                name="contactPhone"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                preserve
                required
                label="Email"
                name="contactEmail"
                rules={[
                  {
                    required: true,
                  },
                  {
                    type: 'email',
                    message: 'Email is not valid',
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Form>
          }
          footer={
            <Space>
              <Button loading={applicationState.loading} onClick={onSubmit} type="primary">
                Done
              </Button>
              <Button
                disabled={applicationState.loading}
                onClick={() => setStep(STEP.COMMUNITY_INFORMATION)}
                type="text"
              >
                Previous
              </Button>
            </Space>
          }
        />
      );
    case STEP.SUCCESS:
      return (
        <ModalLayout
          header={<h6>Your community is now under review</h6>}
          content={
            <div className="flex-grow-1 d-flex flex-column ">
              <div className="paragraph-m">We'll reach out in 24 hours!</div>
              <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                <TwentyFourHourSvg />
              </div>
            </div>
          }
          footer={
            <Button onClick={onComplete} type="primary">
              Done
            </Button>
          }
        />
      );
  }
};

export default CreateFlow;
